/* Core & libraries */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'react-jss';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ErrorMessage, Field, Formik } from 'formik';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import moment from 'helpers/moment';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';

/* Helpers, constants, custom methods, internal libraries */
import { createValidationPolicyYupSchema, useValidationService } from '../../../../resources/validations/validations';
import { useCompaniesService } from '../../../../services/companiesService';
import { useGeneralService } from '../../../../services/generalService';
import { useUserContext } from '../../../../contexts/userContext';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { PAGE_TITLES, TAX_ID_REGEXES } from '../../../../constants/appConstants';
import { HOME_ROUTE } from '../../../../constants/routes.js';
import { useLocaleContext } from '../../../../contexts/localeContext';
import eventEmitter from '../../../../services/eventEmitterService';
import { isValidTaxId, moveByPriority } from '../../../../helpers/general';
import { useDiscountService } from 'services/discountService';

/* Components */
import UCButton from '../../../buttons';
import DeleteCompanyMasterModal from '../../../modals/deleteCompanyMasterModal';
import UCSelect from '../../../ucselect';
import GeneralTabSkeleton from '../../skeletonGeneralTab';
import { ReactComponent as EditEmailSvg } from '../../../../resources/images/edit.svg';
import { ReactComponent as DeleteCompanyIcon } from '../../../../resources/images/delete-company-icon.svg';
import {
  ReactComponent as WarningCompanyDeleteIcon
} from '../../../../resources/images/warning-company-delete-icon.svg';
import UpdateCitiEmailModal from '../../../admin/companyDetailsTabbedNav/updateCitiEmailModal';
import UCPhoneCountrySelector from '../../../ucPhoneCountrySelector';
import GeneralRadioListModal from 'components/modals/generalRadioListModal';
import { ReactComponent as CupIcon } from '../../../../resources/images/choice-cup-icon.svg';
import { ReactComponent as Trophy } from '../../../../resources/images/trophy-01.svg';
import { ReactComponent as SlashIcon } from '../../../../resources/images/slash-circle-02.svg';
import ExpenditureSelector from 'components/expenditureSelector';
import { ReactComponent as InfoIconSvg } from '../../../../resources/images/help.svg';

/* Styles */
import styles, { countrySelect, selectError } from "./styles.js";
import { colors } from "../../../../constants/colors";
import generalStyles from "../../../../resources/styles/general";

const messages = defineMessages({
  namePlaceholderMessage: {
    id: "settings.namePlaceholder",
  },
  countryPlaceholderMessage: {
    id: "settings.countryPlaceholder",
  },
  address1PlaceholderMessage: {
    id: "settings.address1Placeholder",
  },
  address2PlaceholderMessage: {
    id: "settings.address2Placeholder",
  },
  cityPlaceholderMessage: {
    id: "settings.cityPlaceholder",
  },
  statePlaceholderMessage: {
    id: "settings.statePlaceholder",
  },
  postalCodePlaceholderMessage: {
    id: "settings.postalCodePlaceholder",
  },
  referralCodePlaceholderMessage: {
    id: "settings.referralCodePlaceholder",
  },
  taxIdPlaceholderMessage: {
    id: "settings.taxIdPlaceholderCHOICE",
  },
  industryPlaceholderMessage: {
    id: "settings.industryPlaceholder",
  },
  hearAboutUsPlaceholderMessage: {
    id: "settings.hearAboutUsPlaceholder",
  },
  hearAboutUsOtherPlaceholderMessage: {
    id: "settings.hearAboutUsOtherPlaceholder",
  },
  roleAria: {
    id: 'settings.taxId.tooltipTitle',
  },
  roleAria2: {
    id: `settings.taxId.info.CHOICE`,
  },
  businessNrPlaceholderMessage: {
    id: 'settings.businessNrPlaceholder'
  },
});

const GeneralTabCHOICE = (props) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const globalLocale = useLocaleContext();
  const { updateCompany, getCompanyAdmins, getCompanyDetails, getMembershipOptions } = useCompaniesService();
  const { overrideTierProgram } = useDiscountService();
  const { getStates, getCountries } = useGeneralService();

  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState(null);

  const { companyInfoMockedApiValidationObjChoice } = useValidationService();
  const [testSchema, setTestSchema] = useState({});

  const [showTierOverride, setShowTierOverrideModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [states, setStates] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [showUpdateCitiEmailModal, setShowUpdateCitiEmailModal] = useState(false);
  const [fullName, setFullName] = useState("");
  const [countriesData, setCountriesData] = useState([]);
  const [optionsData, setOptionsData] = useState([]);

  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();
  const { checkPermission } = useHasPermission();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const tierHistory = companyDetails?.discount?.currentTier?.tierHistory;

  const fetchCompanyDetails = async () => {
    const response = await getCompanyDetails(companyEntityId);
    setCompanyDetails({
      ...response,
      companyId: companyEntityId,
    });
  };

  useEffect(() => {
    const initUtagData = {
      channel: "content",
      pageName: PAGE_TITLES["settings"],
    };
    initAnalytics(initUtagData, "init call", "view");

    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(companyInfoMockedApiValidationObjChoice);
    setTestSchema(result);
  }, []);

  useEffect(() => {
    const init = async () => {
      // Company details
      const response = await getCompanyDetails(companyEntityId);

      const country = response?.address?.country || "US";
      handleCountryChange(country);

      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      });

      // Company admins
      const responseAdmins = await getCompanyAdmins(companyEntityId);
      setCompanyAdmins(responseAdmins?.usersByRoleDTOList);

      // Countries
      const countriesResponse = await getCountries();
      setCountriesData(moveByPriority(countriesResponse, "value") || []);

      // Options data
      const optionsResponse = await getMembershipOptions([
        "O_COMPANYSETTINGS_INDUSTRY",
        "O_COMPANYSETTINGS_HEARABOUTPROGRAM",
      ]);
      setOptionsData(optionsResponse || []);
    };

    init();
  }, []);

  const getOptionsSorted = (optionsData, optionName) =>
    optionsData
      ?.find((item) => item.optionName === optionName)
      ?.options?.map(({ optionValue, optionText }) => ({
        value: optionValue,
        labelId: optionText,
        label: optionValue,
      })) || [];

  const industryOptions = useMemo(() => getOptionsSorted(optionsData, "O_COMPANYSETTINGS_INDUSTRY"), [optionsData]);

  const hearAboutUsOptions = useMemo(
    () => getOptionsSorted(optionsData, "O_COMPANYSETTINGS_HEARABOUTPROGRAM"),
    [optionsData],
  );

  useEffect(() => {
    const name = companyAdmins?.find((item) => item.userEntityId === companyDetails?.primaryAdminId);
    const fullName = name?.firstName + " " + name?.lastName;
    setFullName(fullName);
  }, [companyAdmins]);

  useEffect(() => {
    const handleCompanyDeletionPending = async () => {
      fetchCompanyDetails();
    };

    eventEmitter.on("companyDeletionPending", handleCompanyDeletionPending);

    return () => {
      eventEmitter.off("companyDeletionPending", handleCompanyDeletionPending);
    };
  }, [companyEntityId, getCompanyDetails]);

  const taxFieldValidation = (values) => {
    let taxId = values.taxId;
    if (companyDetails?.taxIdRequired && !taxId) {
      return <FormattedMessage id="taxIdValidation.choiceTaxRequired" />;
    }
    if (taxId && taxId !== maskedTaxId(companyDetails?.taxId) && !isValidTaxId(taxId, values.countryCode)) {
      return <FormattedMessage id="taxIdValidation.choiceTaxMatches" />;
    }
  };

  const submitForm = async (values, setSubmitting, setFieldError) => {
    const payload = {
      companyId: companyEntityId,
      primaryAdminId: values.primaryContact,
      companyName: values.companyName,
      phoneCountryCode: values.phoneCountryCode,
      phoneNumber: values.phoneNumber,
      address: {
        country: values.countryCode,
        streetAddress: values.streetAddress1,
        streetAddress2: values.streetAddress2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
      industryType: values.industryType,
      hotelExpenditure: values.hotelExpenditure,
      hearAboutUs: values.hearAboutUs,
      hearAboutUsOther: values.hearAboutUsOther,
      numberOfEmployees: values.numberOfEmployees.toString(),
      businessWebsite: values.businessWebsite,
      referralCode: values.referralCode,
    };

    if (checkPermission('F_MANAGE_COMPANY_TAXID') && values.taxId !== maskedTaxId(companyDetails?.taxId)) {
      payload.taxId = values.taxId;
    }
    if (checkPermission("F_MANAGE_COMPANY_MEMBERSHIP_NUMBER")) {
      payload.companyMembershipNumber = values.businessAccountNumber;
    }
    await updateCompany(payload);
    setSubmitting(false);
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    navigate(HOME_ROUTE);
    window.scrollTo(0, 0);
  };

  const handleCountryChange = async (item) => {
    const response = await getStates(item);
    if (response) {
      setStates(response.states);
    }
  };

  const changeTier = async (selectedTier) => {
    const tierCode = companyDetails?.discount?.tierLevels.find((item) => item.tierId === selectedTier)?.tierCode;
    const response = await overrideTierProgram(tierCode, companyDetails.companyId);
    if (response) {
      setShowTierOverrideModal(false);
      fetchCompanyDetails();
    }
  };

  const getStatusIcon = () => {
    if (companyDetails?.discount?.currentTier?.tierId !== 1) {
      return <Trophy />;
    } else return <SlashIcon />;
  };

  const maskedTaxId = (taxNumber) => {
    if (!taxNumber || isNaN(taxNumber)) {
      return taxNumber;
    } else {
      const maskedPart = "*".repeat(5);
      return `${maskedPart}${taxNumber}`;
    }
  };

  return (
    <>
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="settings.titleSection" />
          </div>
          <div className={classes.description}>
            <FormattedMessage id="settings.description" />
          </div>
        </div>
        <div className={classes.btnContainer}>
          {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
            <>
              {!userDetails?.forceUpdateCompanyInfo && (
                <UCButton
                  type="default"
                  className={classes.buttonCancel}
                  onClick={cancelUpdate}
                  text={<FormattedMessage id="settings.cancel" />}
                />
              )}
              <UCButton
                type="primary"
                className={classes.button}
                onClick={handleFormSubmit}
                text={<FormattedMessage id="settings.save" />}
                spinner={{
                  name: "updateCompanySpinner",
                  size: 20,
                  color: colors.white,
                }}
              />
            </>
          ) : (
            <div className={classes.secondRowAuto}>
              <Skeleton count={1} width={68} className={classes.longSkM} />
              <Skeleton count={1} width={68} className={classes.longSk} />
            </div>
          )}
        </div>
      </div>

      <div className={classes.dividerContainer}>
        <hr className={classes.border} />
      </div>
      {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
        <div className={classes.editCompany}>
          {companyDetails && (
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                primaryContact: companyDetails?.primaryAdminId,
                companyName: companyDetails?.companyName,
                countryCode: companyDetails?.address?.country,
                streetAddress1: companyDetails?.address?.streetAddress,
                streetAddress2: companyDetails?.address?.streetAddress2 ? companyDetails?.address?.streetAddress2 : "",
                city: companyDetails?.address?.city,
                state: companyDetails?.address?.state,
                postalCode: companyDetails?.address?.postalCode,
                phoneCountryCode: companyDetails?.phoneCountryCode ? companyDetails?.phoneCountryCode : "US",
                phoneNumber: companyDetails?.phoneNumber?.trim() || "",
                taxId: companyDetails?.taxId ? maskedTaxId(companyDetails?.taxId) : "",
                businessAccountNumber: companyDetails?.companyMembershipNumber,
                travelManager: fullName ? fullName : "",
                industryType: companyDetails?.industryType || "",
                hotelExpenditure: companyDetails?.hotelExpenditure || "",
                hearAboutUs: companyDetails?.hearAboutUs || "",
                hearAboutUsOther: companyDetails?.hearAboutUsOther || "",
                numberOfEmployees: companyDetails?.numberOfEmployees || "",
                businessWebsite: companyDetails?.businessWebsite || "",
                referralCode: companyDetails?.referralCode || "",
              }}
              validationSchema={testSchema}
              onSubmit={(values, { setSubmitting, setFieldError }) => {
                submitForm(values, setSubmitting, setFieldError);
              }}
            >
              {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                resetForm,
                isSubmitting,
                setFieldValue,
                validateField,
                enableReinitialize,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.contact" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.contactDescription" />
                        </div>
                      </div>
                      {companyAdmins?.length > 0 && !companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <Field
                              placeholder={"primaryContact"}
                              type="select"
                              name="primaryContact"
                              id="primaryContact"
                              render={({ form }) => (
                                <div>
                                  <label htmlFor="primaryContact" className={classes.label}>
                                    <FormattedMessage id="settings.primaryAdmin" />
                                  </label>
                                  <UCSelect
                                    className={classes.roles}
                                    displayName={<FormattedMessage id="settings.primaryAdmin" />}
                                    options={companyAdmins}
                                    defaultValue={companyAdmins?.find(
                                      (item) => item.userEntityId === values.primaryContact,
                                    )}
                                    onChange={(item) => form.setFieldValue("primaryContact", item.userEntityId)}
                                    getOptionLabel={(option) => {
                                      return (
                                        <div className={classes.adminLabel}>
                                          <div className={classes.adminName}>
                                            {option.firstName} {option.lastName}
                                          </div>
                                        </div>
                                      );
                                    }}
                                    getOptionValue={(option) => option.userEntityId}
                                    disabled={!companyDetails?.canUpdatePrimaryAdmin}
                                  />
                                  <ErrorMessage name="primaryContact" component="span" className={classes.error} />
                                  <p className={classes.pContactSubtext}>
                                    <FormattedMessage id="settings.primaryContact.subText" />
                                  </p>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputMailContainer}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="admin.editCompany.primaryAdmin" />
                              </label>
                            </div>
                            <div className={classes.editDiv}>
                              <Field
                                placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                                type="text"
                                name="travelManager"
                                id="travelManager"
                                value={fullName}
                                className={errors.travelManager && touched.travelManager ? "input-error" : null}
                                disabled={true}
                              />
                              <UCButton
                                onClick={() => setShowUpdateCitiEmailModal(true)}
                                className={classes.editButton}
                                iconEnd={<EditEmailSvg />}
                              />
                            </div>
                            <ErrorMessage name="travelManeger" component="span" className={classes.error} />
                            <span className={classes.taxIdNote}>
                              <FormattedMessage id={"admin.updateCitiEmailText"} />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="settings.location" />
                        </div>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.update" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="companyName" className={classes.label}>
                              <FormattedMessage id="settings.companyName" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                              type="text"
                              name="companyName"
                              id="companyName"
                              className={errors.companyName && touched.companyName ? "input-error" : null}
                              disabled={!checkPermission("F_COMPANYSETTINGS_EDITNAME")}
                            />
                            <ErrorMessage name="companyName" component="span" className={classes.error} />
                          </div>
                          {checkPermission("F_COMPANYSETTINGS_COUNTRY_VIEW") && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="country" className={classes.label}>
                                <FormattedMessage id="settings.country" />
                              </label>
                              <Field
                                type="select"
                                name="countryCode"
                                id="countryCode"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      isSearchable={true}
                                      className={classes.roles}
                                      styles={errors.countryCode && touched.countryCode ? { ...countrySelect, ...selectError } : countrySelect}
                                      displayName={<FormattedMessage id="settings.countryPlaceholder" />}
                                      options={countriesData}
                                      defaultValue={countriesData.find((item) => item.value === values.countryCode)}
                                      value={values.countryCode}
                                      getOptionLabel={(option) => option.label}
                                      onBlur={() => setFieldTouched("countryCode", true)}
                                      onChange={(item) => {
                                        form.setFieldValue("countryCode", item.value);
                                        handleCountryChange(item.value);
                                        {
                                          item.value !== values.countryCode ? form.setFieldValue("state", " ") : null;
                                        }
                                      }}
                                    />
                                    <ErrorMessage name="countryCode" component="span" className={classes.error} />
                                  </div>
                                )}
                                className={errors.countryCode && touched.countryCode ? "input-error" : null}
                              />
                            </div>
                          )}
                          <div className={classes.inputContainer}>
                            <label htmlFor="taxId" className={classes.label}>
                                {values.countryCode === 'CA' ? (
                                  <FormattedMessage id='settings.businessNr' />
                                ) : (
                                  <FormattedMessage id="settings.taxId.titleCHOICE" />
                                )}
                              {companyDetails?.taxIdRequired && <span aria-hidden="true">*</span>}
                              <OverlayTrigger
                                placement="top"
                                trigger={['hover', 'focus']}
                                overlay={
                                  <Tooltip className={`${general.main} ${classes.tooltip}`}>
                                    <div className={classes.infoDescriptionAdmin}>
                                      {intl.formatMessage({ id: `settings.taxId.info.${providerCode}` })}
                                    </div>
                                  </Tooltip>
                                }
                              >
                                <InfoIconSvg
                                  className={classes.infoIcon}
                                  tabIndex="0"
                                  role="tooltip"
                                  aria-label={`${intl.formatMessage(messages.roleAria)} ${intl.formatMessage(messages.roleAria2)}`}
                                />
                              </OverlayTrigger>
                            </label>
                            <Field
                              placeholder={
                                values.countryCode === 'CA'
                                  ? intl.formatMessage(messages.businessNrPlaceholderMessage)
                                  : intl.formatMessage(messages.taxIdPlaceholderMessage)
                              }
                              type="text"
                              name="taxId"
                              id="taxId"
                              validate={() =>
                                checkPermission("F_MANAGE_COMPANY_TAXID") ? taxFieldValidation(values) : null
                              }
                              className={errors.taxId && touched.taxId ? "input-error" : null}
                              disabled={
                                !checkPermission("F_MANAGE_COMPANY_TAXID") ||
                                companyDetails?.companyStatus === "REMOVED"
                              }
                              onFocus={() => {
                                if (maskedTaxId(companyDetails?.taxId) === values.taxId) {
                                  setFieldValue("taxId", "")
                                }
                              }}
                              onBlur={() => {
                                setFieldTouched("taxId", true);
                                if (!values.taxId && !companyDetails?.taxIdRequired) {
                                  setFieldValue("taxId", maskedTaxId(companyDetails?.taxId)).then(() =>
                                      validateField("taxId")
                                  );
                                }
                              }}
                            />
                            <ErrorMessage name="taxId" component="span" className={classes.error} />
                            {TAX_ID_REGEXES[values.countryCode]?.expectedFormat && (
                              <span id="taxId-description" className={classes.taxIdNote}>
                                <FormattedMessage
                                  id={`taxIdNote`}
                                  values={{ format: TAX_ID_REGEXES[values.countryCode]?.expectedFormat }}
                                />
                              </span>
                            )}
                          </div>
                          {checkPermission("F_COMPANYSETTINGS_PHONE_VIEW") && (
                            <div className={classes.phoneInputContainer}>
                              <label htmlFor="phoneNumber" className={classes.label}>
                                <FormattedMessage id="settings.phoneNumber" />
                                <span aria-hidden="true">*</span>
                              </label>
                              <UCPhoneCountrySelector
                                providerCode={providerCode}
                                errors={errors}
                                touched={touched}
                                values={values}
                                disabled={!checkPermission("F_COMPANYSETTINGS_PHONE_EDIT")}
                              />
                              <ErrorMessage name="phoneNumber" component="span" className={classes.error} />
                            </div>
                          )}
                          {checkPermission("F_COMPANYSETTINGS_ADDRESS1_VIEW") && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="streetAddress1" className={classes.label}>
                                <FormattedMessage id="settings.address1" />
                                <span aria-hidden="true">*</span>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.address1PlaceholderMessage)}
                                type="text"
                                name="streetAddress1"
                                id="streetAddress1"
                                className={errors.streetAddress1 && touched.streetAddress1 ? "input-error" : null}
                                disabled={!checkPermission("F_COMPANYSETTINGS_ADDRESS1_EDIT")}
                              />
                              <ErrorMessage name="streetAddress1" component="span" className={classes.error} />
                            </div>
                          )}
                          {checkPermission("F_COMPANYSETTINGS_ADDRESS2_VIEW") && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="streetAddress2" className={classes.label}>
                                <FormattedMessage id="settings.address2" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.address2PlaceholderMessage)}
                                type="text"
                                name="streetAddress2"
                                id="streetAddress2"
                                className={errors.streetAddress2 && touched.streetAddress2 ? "input-error" : null}
                                disabled={!checkPermission("F_COMPANYSETTINGS_ADDRESS2_EDIT")}
                              />
                              <ErrorMessage name="streetAddress2" component="span" className={classes.error} />
                            </div>
                          )}
                          {checkPermission("F_COMPANYSETTINGS_CITY_VIEW") && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="city" className={classes.label}>
                                <FormattedMessage id="settings.city" />
                                <span aria-hidden="true">*</span>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.cityPlaceholderMessage)}
                                type="text"
                                name="city"
                                id="city"
                                className={errors.city && touched.city ? "input-error" : null}
                                disabled={!checkPermission("F_COMPANYSETTINGS_CITY_EDIT")}
                              />
                              <ErrorMessage name="city" component="span" className={classes.error} />
                            </div>
                          )}
                          {states && (
                            <div className={classes.inputContainer}>
                              <label htmlFor="state" className={classes.label}>
                                <FormattedMessage id="settings.state" />
                                <span aria-hidden="true">*</span>
                              </label>
                              <Field
                                type="select"
                                name="state"
                                id="state"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      styles={errors.state && touched.state && selectError}
                                      displayName={<FormattedMessage id="settings.statePlaceholder" />}
                                      value={values.state}
                                      options={states}
                                      onBlur={() => setFieldTouched("state", true)}
                                      defaultValue={states.find((item) => item.value === values.state)}
                                      onChange={(item) => form.setFieldValue("state", item.value)}
                                      isSearchable={true}
                                    />
                                    <ErrorMessage name="state" component="span" className={classes.error} />
                                  </div>
                                )}
                                className={errors.state && touched.state ? "input-error" : null}
                              />
                            </div>
                          )}
                          {checkPermission("F_COMPANYSETTINGS_ZIP_VIEW") && (
                            <div className={`${classes.inputContainer} ${classes.lastInputContainer}`}>
                              <label htmlFor="postalCode" className={classes.label}>
                                <FormattedMessage id="settings.postalCode" />
                                <span aria-hidden="true">*</span>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.postalCodePlaceholderMessage)}
                                type="postalCode"
                                name="postalCode"
                                id="postalCode"
                                className={errors.postalCode && touched.postalCode ? "input-error" : null}
                                disabled={!checkPermission("F_COMPANYSETTINGS_ZIP_EDIT")}
                              />
                              <ErrorMessage name="postalCode" component="span" className={classes.error} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.border} />
                    </div>

                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className={classes.location}>
                            <FormattedMessage id="settings.membership" />
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="settings.membershipDescription" />
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            {checkPermission("F_MANAGE_COMPANY_MEMBERSHIP_NUMBER") &&
                              <div className={classes.inputContainer}>
                                <label htmlFor="businessAccountNumber" className={classes.label}>
                                  <FormattedMessage id="settings.companyID" />
                                </label>
                                <Field
                                  autoComplete="off"
                                  type="text"
                                  name="businessAccountNumber"
                                  id="businessAccountNumber"
                                  className={errors.businessAccountNumber && touched.businessAccountNumber ? "input-error" : null}
                                  disabled={true}
                                />
                                <ErrorMessage name="businessAccountNumber" component="span" className={classes.error} />
                              </div>
                            }
                            {industryOptions.length > 0 && checkPermission("F_COMPANYSETTINGS_INDUSTRY_VIEW") && (
                              <div className={classes.inputContainer}>
                                <label htmlFor="industryType" className={classes.label}>
                                  <FormattedMessage id="settings.industry" />
                                </label>
                                <Field
                                  placeholder={intl.formatMessage(messages.industryPlaceholderMessage)}
                                  type="select"
                                  name="industryType"
                                  id="industryType"
                                >
                                  {({ form }) => (
                                    <>
                                      <UCSelect
                                        isSearchable={false}
                                        fieldHasError={errors.industryType && touched.industryType}
                                        displayName={<FormattedMessage id="settings.industryPlaceholder" />}
                                        value={values.industryType}
                                        options={industryOptions}
                                        onChange={(item) => form.setFieldValue("industryType", item.value)}
                                        disabled={!checkPermission("F_COMPANYSETTINGS_INDUSTRY_EDIT")}
                                      />
                                      <ErrorMessage name="industryType" component="span" className={classes.error} />
                                    </>
                                  )}
                                </Field>
                              </div>
                            )}
                            {checkPermission("F_COMPANYSETTINGS_SPEND_VIEW") && (
                              <div className={classes.expenditureInputContainer}>
                                <label htmlFor="hotelExpenditure" className={classes.label}>
                                  <FormattedMessage id="settings.expenditure" />
                                </label>
                                <ExpenditureSelector
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  disabled={!checkPermission("F_COMPANYSETTINGS_SPEND_EDIT")}
                                />
                                <ErrorMessage name="hotelExpenditure" component="span" className={classes.error} />
                              </div>
                            )}
                            {hearAboutUsOptions.length > 0 &&
                              checkPermission("F_COMPANYSETTINGS_ABOUTPROGRAM_VIEW") && (
                                <div className={classes.inputContainer}>
                                  <label htmlFor="hearAboutUs" className={classes.label}>
                                    <FormattedMessage id="settings.hearAboutUs" />
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.hearAboutUsPlaceholderMessage)}
                                    type="select"
                                    name="hearAboutUs"
                                    id="hearAboutUs"
                                  >
                                    {({ form }) => (
                                      <>
                                        <UCSelect
                                          isSearchable={false}
                                          fieldHasError={errors.hearAboutUs && touched.hearAboutUs}
                                          displayName={<FormattedMessage id="settings.hearAboutUsPlaceholder" />}
                                          value={values.hearAboutUs}
                                          options={hearAboutUsOptions}
                                          onChange={(item) => {
                                            form.setFieldValue("hearAboutUs", item.value);
                                          }}
                                          disabled={!checkPermission("F_COMPANYSETTINGS_ABOUTPROGRAM_EDIT")}
                                        />
                                        <ErrorMessage name="hearAboutUs" component="span" className={classes.error} />
                                      </>
                                    )}
                                  </Field>
                                </div>
                              )}
                            {values.hearAboutUs === "Other" &&
                              checkPermission("F_COMPANYSETTINGS_ABOUTPROGRAM_VIEW") && (
                                <div className={classes.hearAboutUsContainer}>
                                  <Field
                                    placeholder={intl.formatMessage(messages.hearAboutUsOtherPlaceholderMessage)}
                                    type="text"
                                    as="textarea"
                                    name="hearAboutUsOther"
                                    id="hearAboutUsOther"
                                    autoComplete="off"
                                    className={
                                      errors.hearAboutUsOther && touched.hearAboutUsOther ? "input-error" : null
                                    }
                                    disabled={!checkPermission("F_COMPANYSETTINGS_ABOUTPROGRAM_EDIT")}
                                  />
                                  <ErrorMessage name="hearAboutUsOther" component="span" className={classes.error} />
                                </div>
                              )}
                            {checkPermission("F_COMPANYSETTINGS_NUMBEROFEMPLOYEES_VIEW") && (
                              <div className={classes.inputContainer}>
                                <label htmlFor="numberOfEmployees" className={classes.label}>
                                  <FormattedMessage id="settings.employees" />
                                </label>
                                <Field
                                  autoComplete="off"
                                  type="number"
                                  inputMode="numeric"
                                  name="numberOfEmployees"
                                  id="numberOfEmployees"
                                  className={
                                    errors.numberOfEmployees && touched.numberOfEmployees ? "input-error" : null
                                  }
                                  disabled={!checkPermission("F_COMPANYSETTINGS_NUMBEROFEMPLOYEES_EDIT")}
                                />
                                <ErrorMessage name="numberOfEmployees" component="span" className={classes.error} />
                              </div>
                            )}
                            {checkPermission("F_COMPANYSETTINGS_WEBSITE_VIEW") && (
                              <div className={classes.inputContainer}>
                                <label htmlFor="businessWebsite" className={classes.label}>
                                  <FormattedMessage id="settings.businessWebsite" />
                                </label>
                                <Field
                                  autoComplete="off"
                                  type="text"
                                  name="businessWebsite"
                                  id="businessWebsite"
                                  className={errors.businessWebsite && touched.businessWebsite ? "input-error" : null}
                                  disabled={!checkPermission("F_COMPANYSETTINGS_WEBSITE_EDIT")}
                                />
                                <ErrorMessage name="businessWebsite" component="span" className={classes.error} />
                              </div>
                            )}
                            {checkPermission("F_COMPANYSETTINGS_REFERRALCODE_VIEW") && (
                              <div className={classes.inputContainer}>
                                <label htmlFor="referralCode" className={classes.label}>
                                  <FormattedMessage id="settings.referralCode" />
                                </label>
                                <Field
                                  autoComplete="off"
                                  type="text"
                                  name="referralCode"
                                  id="referralCode"
                                  className={errors.referralCode && touched.referralCode ? "input-error" : null}
                                  disabled={!checkPermission("F_COMPANYSETTINGS_REFERRALCODE_EDIT")}
                                />
                                <ErrorMessage name="referralCode" component="span" className={classes.error} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {userDetails?.manageCompany && (
                      <>
                        <div className={classes.dividerContainer}>
                          <hr className={classes.border} />
                        </div>

                        <div className={`row ${classes.containerGap}`}>
                          <div className="col-12 col-xl-4">
                            <div className={classes.location}>
                              <FormattedMessage id="override.tier.details" />
                            </div>
                            <div className={classes.description}>
                              <FormattedMessage id="override.tier.desc" />
                            </div>
                          </div>
                          <div className={`col-12 col-xl-8 ${classes.colContentWrapper}`}>
                            <div className={classes.statusDiv}>
                              {getStatusIcon()}
                              <div className={classes.textDiv}>
                                <div className={classes.thrTitle}>
                                  {companyDetails?.discount?.currentTier?.tierCode}
                                </div>
                                <div className={classes.thrDescription}>
                                  <FormattedMessage
                                    id="override.tier.changed.by"
                                    values={{
                                      date: moment(tierHistory?.[0]?.dateTime)
                                        .locale(globalLocale)
                                        .format("M/D/YY, h:mm A"),
                                      name: tierHistory?.[0]?.userName,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            <UCButton
                              type="link"
                              className={
                                companyDetails?.companyStatus === "REMOVED"
                                  ? classNames(classes.tierStatusBtn, classes.disabledThresholdStatusBtn)
                                  : classes.tierStatusBtn
                              }
                              text={<FormattedMessage id="override.tier.btn" />}
                              onClick={() => setShowTierOverrideModal(true)}
                              disabled={companyDetails?.companyStatus === "REMOVED"}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {checkPermission("F_COMPANY_DELETE") && companyDetails?.companyStatus && (
                    <div>
                      <div className={classes.formCont}>
                        <div className="row">
                          <div className="col-12 col-md-4">
                            <div className={classes.location}>
                              <FormattedMessage id="settings.deleteBusiness" />
                            </div>
                            <div className={classes.description}>
                              <FormattedMessage id="settings.deleteBusinessDescription" />
                            </div>
                          </div>

                          {companyDetails?.companyStatus === "ACTIVE" && (
                            <div className="col-12 col-md-8">
                              <UCButton
                                type="danger"
                                className={classes.deleteCompanyButton}
                                text={<FormattedMessage id="settings.deleteCompanyButton" />}
                                onClick={() => setShowDeleteModal(true)}
                              />
                            </div>
                          )}

                          {companyDetails?.companyStatus === "PENDING_REMOVAL" && (
                            <div className="col-12 col-md-8">
                              <div className="row">
                                <div className={`col-12 col-md-auto ${classes.warningIconContainer}`}>
                                  <WarningCompanyDeleteIcon />
                                </div>
                                <div className="col-12 col-md">
                                  <p className={classes.accountClosedTitle}>
                                    <FormattedMessage id="settings.accountClosedTitle" />
                                  </p>
                                  <p className={classes.accountClosedDescription}>
                                    <FormattedMessage
                                      id="settings.accountClosedPendingDescription"
                                      values={{
                                        atmName: (
                                          <span className={classes.accountClosedCustomValues}>
                                            {companyDetails?.removalRequestedByFirstName}{" "}
                                            {companyDetails?.removalRequestedByLastName}
                                          </span>
                                        ),
                                        removalDate: (
                                          <span className={classes.accountClosedCustomValues}>
                                            {companyDetails?.removalDate}
                                          </span>
                                        ),
                                      }}
                                    />
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border} />
                      </div>
                    </div>
                  )}

                  <Row>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          {!userDetails?.forceUpdateCompanyInfo && (
                            <UCButton
                              type="default"
                              className={classes.buttonCancel}
                              onClick={cancelUpdate}
                              text={<FormattedMessage id="settings.cancel" />}
                            />
                          )}
                          <UCButton
                            type="primary"
                            className={classes.button}
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="settings.save" />}
                            spinner={{
                              name: "updateCompanySpinner",
                              size: 20,
                              color: colors.white,
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>
          )}

          {
            showDeleteModal &&
              <DeleteCompanyMasterModal
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                modalIcon={<DeleteCompanyIcon />}
                setSelectedRow={(value) => console.log(value)}
              />
          }

          <GeneralRadioListModal
            isOpen={showTierOverride}
            onClose={() => setShowTierOverrideModal(false)}
            onConfirm={(selected) => changeTier(selected)}
            initialValue={companyDetails?.discount?.currentTier?.tierId}
            title={<FormattedMessage id="override.tier.change" />}
            description={<FormattedMessage id="override.tier.select" />}
            icon={<CupIcon />}
            options={companyDetails?.discount?.tierLevels}
            confirmText={<FormattedMessage id="override.tier.confirm" />}
            cancelText={<FormattedMessage id="override.tier.cancel" />}
            className={classes.overrideTierModal}
            getValue={(tier) => tier.tierId}
            getLabel={(tier) => (
              <div className={classes.radioLabelContainer}>
                <span className={classes.tierCodeText}>{tier.tierCode}</span>
                <span className={classes.descriptionTierText}>
                  <FormattedMessage
                    id="discount.change.tier.description"
                    values={{ discount: tier.discount, minimumNights: tier.minimumNights }}
                  />
                </span>
              </div>
            )}
            confirmDisabled={(selectedTierId) => {
              const currentTierId = companyDetails?.discount?.currentTier?.tierId;
              return selectedTierId <= currentTierId; // disables current tier and all tiers before that (Choice admin can only upgrade tier)
            }}
            radioListButtonSpinner="overrideTierProgramSpinner"
          />

          {companyDetails?.mustChangeEmailAddress && (
            <UpdateCitiEmailModal
              showUpdateCitiEmailModal={showUpdateCitiEmailModal}
              setShowUpdateCitiEmailModal={setShowUpdateCitiEmailModal}
              companyDetails={companyDetails}
            />
          )}
        </div>
      ) : (
        <GeneralTabSkeleton />
      )}
    </>
  );
};

export default GeneralTabCHOICE;
